import { QuoteServiceLineChangeReview } from './QuoteServiceLineChangeReview';
import { parseQuoteServiceLineReview } from './quoteServiceLinePayload';
import { QuoteServiceAddReview } from './QuoteServiceAddReview';
import { parseQuoteServiceAddReview } from './quoteServiceAddPayload';
import { useState } from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ConfirmationPresenter, ConfirmationRetry } from '@/stores/assistantStore';
import { WorkConfirmationReview } from './WorkConfirmationReview';
import { withNoun } from './presenterNoun';
import { parseWorkReview } from './workReviewPayload';
import { QuoteConfirmationReview } from './QuoteConfirmationReview';
import { parseQuoteConfirmationReview } from './quoteConfirmationPayload';
import { QuoteSendConfirmationReview } from './QuoteSendConfirmationReview';
import { parseQuoteSendReview } from './quoteSendReviewContract';
import { QuoteItemChangeReview } from './QuoteItemChangeReview';
import { parseQuoteItemChangeReview } from './quoteItemChangePayload';
import { ConfirmationListFields, ENUM_VALUE, listFieldsOf } from './ConfirmationListFields';

interface ConfirmationCardProps {
  /** The server's composed summary — the transcript row, and the fallback text. */
  description: string;
  toolName?: string | undefined;
  /**
   * The harness path's localised rendering (#2180): `t(key, params)` from the
   * `assistant` namespace. Absent on legacy cards.
   */
  presenter?: ConfirmationPresenter | undefined;
  params: Record<string, unknown>;
  isLoading: boolean;
  /** Set when the server refused the last confirm but kept the plan (#2821). */
  retry?: ConfirmationRetry | undefined;
  /** `fields` carries values the user set on the card (#2821), e.g. the confirmed quote receipt time. */
  onConfirm: (fields?: ConfirmFields) => void;
  onCancel: () => void;
}

/** Values the human set on the card itself; sent with Confirm, never taken from the model. */
export interface ConfirmFields {
  readonly approvalReceivedAt?: string;
}

const UNFILLED_PLACEHOLDER = /\{\{\s*[\w.]+\s*\}\}/;

/**
 * One field of an UPDATE plan, as `payload.fieldChanges` carries it (#2199):
 * `{ field, from, to }` with the ROUTE's field name and the CURRENT value
 * (`null` when the row has none). Rendered so the user confirming "update 1
 * field" sees WHICH field and WHAT value — a count alone is not informed
 * consent (review finding). Field labels come from `confirmation.fields.*`
 * with the raw name as the fallback, so an unlabelled field is still shown.
 */
interface FieldChange {
  field: string;
  /**
   * #3820: generated Tier B updates name the route field's type, and their
   * values keep it (a number, a boolean, a date). Absent on the hand-built
   * ports, whose values are always text.
   */
  type?: FieldType;
  from: FieldValue;
  /** `null` is a clear. */
  to: FieldValue;
}

type FieldValue = string | number | boolean | null;

const FIELD_TYPES = ['string', 'number', 'integer', 'boolean', 'enum', 'date', 'datetime', 'uuid'] as const;
type FieldType = (typeof FIELD_TYPES)[number];

/** A real calendar day as `YYYY-MM-DD`: 2027-02-31 does not roll over into March, it is refused. */
function isCalendarDay(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return !Number.isNaN(day.getTime()) && day.toISOString().slice(0, 10) === value;
}

const DATETIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|[+-](\d{2}):?(\d{2}))$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * An instant in ISO form with `Z` or an offset, on a real calendar day and
 * clock time: 2027-02-31T12:00:00Z and a bare date are refused, not rolled over.
 */
function isInstant(value: string): boolean {
  const match = DATETIME.exec(value);
  if (!match || !isCalendarDay(match[1] ?? '')) return false;
  const at = (index: number): number => Number(match[index] ?? 0);
  return at(2) <= 23 && at(3) <= 59 && at(4) <= 59 && at(6) <= 23 && at(7) <= 59 && !Number.isNaN(Date.parse(value));
}

/** Whether a value is what its declared type carries (or a clear). Text only without a type. */
function valueFits(value: unknown, type: FieldType | undefined): value is FieldValue {
  if (value === null) return true;
  if (type === 'integer') return typeof value === 'number' && Number.isInteger(value);
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'boolean') return typeof value === 'boolean';
  if (type === 'date') return typeof value === 'string' && isCalendarDay(value);
  if (type === 'datetime') return typeof value === 'string' && isInstant(value);
  if (type === 'uuid') return typeof value === 'string' && UUID.test(value);
  return typeof value === 'string';
}

/**
 * Every significant digit, from the number's own shortest round-trip text:
 * the locale's grouping and decimal separator, no rounding. An exponent form
 * (1e-21) stays as it is, so 1e-21 and 2e-21 never look alike.
 */
function exactNumber(value: number, language: string): string {
  const raw = String(value);
  if (/e/i.test(raw)) return raw;
  const negative = raw.startsWith('-');
  const [whole = '0', fraction] = (negative ? raw.slice(1) : raw).split('.');
  const format = new Intl.NumberFormat(language);
  const decimal = format.formatToParts(1.5).find((part) => part.type === 'decimal')?.value ?? '.';
  return `${negative ? '-' : ''}${format.format(BigInt(whole))}${fraction === undefined ? '' : `${decimal}${fraction}`}`;
}

/** Date, time to the second (milliseconds when there are any) and the zone it is shown in. */
function instantText(value: string, language: string): string {
  const instant = new Date(value);
  return new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    ...(instant.getUTCMilliseconds() !== 0 ? { fractionalSecondDigits: 3 as const } : {}),
  }).format(instant);
}

/** Locale formatting that never throws: anything it cannot format is shown as its raw text. */
function formatted(raw: string, format: () => string): string {
  try {
    return format();
  } catch {
    return raw;
  }
}

const FIELD_NAME = /^[A-Za-z][A-Za-z0-9]*$/;
/** Names that reach Object.prototype through the bundle lookup; never a real column. */
const RESERVED_FIELD_NAMES = new Set(['constructor', 'prototype', 'toString', 'valueOf', 'hasOwnProperty']);

/**
 * `absent`: the payload carries no diff (a CREATE) — the headline is the whole
 * card. `invalid`: it carries one this client cannot read (a malformed or
 * duplicated entry, an empty list). An UPDATE whose diff cannot be shown must
 * not be confirmable: a count without values is exactly what round one of
 * review refused, so the card fails closed and says so (review round 2).
 */
type ChangesOf =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'diff'; readonly changes: readonly FieldChange[] };

/**
 * The payload key is deliberately specific (`fieldChanges`, not `changes`):
 * this card renders EVERY tool's pending payload, and a later port whose
 * payload happens to carry a `changes` array of another shape must not have
 * its Confirm disabled by this reader (review round 2).
 */
const FIELD_CHANGES_KEY = 'fieldChanges';

function changesOf(params: Record<string, unknown>): ChangesOf {
  if (!(FIELD_CHANGES_KEY in params)) return { kind: 'absent' };
  const raw = params[FIELD_CHANGES_KEY];
  if (!Array.isArray(raw) || raw.length === 0) return { kind: 'invalid' };
  const changes: FieldChange[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') return { kind: 'invalid' };
    const { field, from, to, type } = item as Record<string, unknown>;
    if (typeof field !== 'string' || !FIELD_NAME.test(field) || RESERVED_FIELD_NAMES.has(field) || seen.has(field)) {
      return { kind: 'invalid' };
    }
    if (type !== undefined && !(FIELD_TYPES as readonly unknown[]).includes(type)) return { kind: 'invalid' };
    const declared = type as FieldType | undefined;
    if (!valueFits(from, declared) || !valueFits(to, declared)) return { kind: 'invalid' };
    seen.add(field);
    changes.push({ field, from, to, ...(declared ? { type: declared } : {}) });
  }
  return { kind: 'diff', changes };
}

const MISSING_LABEL = '\u0000missing';

/**
 * #3904: an enum value is shown by its label, `confirmation.enumValues.<field>.<value>`,
 * never as the raw identifier. Undefined when no bundle has one: the card then fails closed.
 */
function enumLabel(t: TFunction<'assistant'>, field: string, value: string): string | undefined {
  if (!ENUM_VALUE.test(value) || RESERVED_FIELD_NAMES.has(value)) return undefined;
  const label: unknown = t(`confirmation.enumValues.${field}.${value}`, { defaultValue: MISSING_LABEL, returnObjects: true });
  return typeof label === 'string' && label !== MISSING_LABEL ? label : undefined;
}

/** Whether every enum value of these entries has a label (a clear needs none). */
function enumsLabelled(
  t: TFunction<'assistant'>,
  entries: readonly { field: string; type?: FieldType | undefined; values: readonly FieldValue[] }[],
): boolean {
  return entries.every(
    (entry) =>
      entry.type !== 'enum' ||
      entry.values.every((value) => value === null || (typeof value === 'string' && enumLabel(t, entry.field, value) !== undefined)),
  );
}

function valueText(t: TFunction<'assistant'>, language: string, value: FieldValue, type?: FieldType, field?: string): string {
  if (value === null) return t('confirmation.noValue');
  if (type === 'enum' && typeof value === 'string' && field !== undefined) return enumLabel(t, field, value) ?? value;
  if (typeof value === 'boolean') return t(value ? 'confirmation.booleanTrue' : 'confirmation.booleanFalse');
  // Full precision: Intl's default rounding would show 1.2341 and 1.2344 alike.
  if (typeof value === 'number') return formatted(String(value), () => exactNumber(value, language));
  if (value.trim() === '') return t('confirmation.blankValue');
  // A calendar day is shown as that day, never shifted by the viewer's zone.
  if (type === 'date')
    return formatted(value, () => new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`)));
  if (type === 'datetime') return formatted(value, () => instantText(value, language));
  return value;
}

function changeText(t: TFunction<'assistant'>, language: string, change: FieldChange): string {
  const field = t(`confirmation.fields.${change.field}`, { defaultValue: change.field });
  const from = valueText(t, language, change.from, change.type, change.field);
  const to = valueText(t, language, change.to, change.type, change.field);
  return t('confirmation.change', {
    replace: { field, from, to },
    defaultValue: `${field}: ${from} → ${to}`,
  });
}

/**
 * #3831: what the server writes for a field a generated create does not
 * send, shown so the human sees the whole record (`payload.fieldDefaults`).
 * Same field-name and type rules as the diff; a malformed entry fails closed.
 */
interface FieldDefault {
  field: string;
  type: FieldType;
  value: FieldValue;
}

type DefaultsOf =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'defaults'; readonly defaults: readonly FieldDefault[] };

function defaultsOf(params: Record<string, unknown>): DefaultsOf {
  if (!('fieldDefaults' in params)) return { kind: 'absent' };
  const raw = params['fieldDefaults'];
  if (!Array.isArray(raw)) return { kind: 'invalid' };
  const defaults: FieldDefault[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') return { kind: 'invalid' };
    const { field, type, value } = item as Record<string, unknown>;
    if (typeof field !== 'string' || !FIELD_NAME.test(field) || RESERVED_FIELD_NAMES.has(field) || seen.has(field)) {
      return { kind: 'invalid' };
    }
    if (!(FIELD_TYPES as readonly unknown[]).includes(type)) return { kind: 'invalid' };
    if (!valueFits(value, type as FieldType)) return { kind: 'invalid' };
    seen.add(field);
    defaults.push({ field, type: type as FieldType, value });
  }
  return { kind: 'defaults', defaults };
}

/**
 * #3904: fields a generated operation ALWAYS sends with a reviewed value
 * (`payload.pinnedFields`), shown as fixed so the human sees every value the
 * route receives. Same field-name and type rules as the defaults; a malformed
 * entry fails closed. A clear is never a pin.
 */
function pinnedFieldsOf(params: Record<string, unknown>): DefaultsOf {
  if (!('pinnedFields' in params)) return { kind: 'absent' };
  const parsed = defaultsOf({ fieldDefaults: params['pinnedFields'] });
  if (parsed.kind !== 'defaults') return { kind: 'invalid' };
  if (parsed.defaults.length === 0 || parsed.defaults.some((entry) => entry.value === null)) return { kind: 'invalid' };
  return parsed;
}

function pinnedText(t: TFunction<'assistant'>, language: string, entry: FieldDefault): string {
  const field = t(`confirmation.fields.${entry.field}`, { defaultValue: entry.field });
  const value = valueText(t, language, entry.value, entry.type, entry.field);
  return t('confirmation.pinnedValue', { replace: { field, value }, defaultValue: `${field}: ${value}` });
}

function defaultText(t: TFunction<'assistant'>, language: string, entry: FieldDefault): string {
  const field = t(`confirmation.fields.${entry.field}`, { defaultValue: entry.field });
  const value = valueText(t, language, entry.value, entry.type, entry.field);
  return t('confirmation.defaultValue', { replace: { field, value }, defaultValue: `${field}: ${value}` });
}

/**
 * #3879: the identifying fields of a record a generated delete removes
 * (`payload.recordFields`), shown under the headline. Same field-name and
 * type rules as the diff; a malformed entry fails closed. Empty is valid:
 * the headline's label is then the whole identification.
 */
type RecordFieldsOf =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'fields'; readonly fields: readonly FieldDefault[] };

function recordFieldsOf(params: Record<string, unknown>): RecordFieldsOf {
  if (!('recordFields' in params)) return { kind: 'absent' };
  const raw = params['recordFields'];
  if (!Array.isArray(raw)) return { kind: 'invalid' };
  const fields: FieldDefault[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') return { kind: 'invalid' };
    const { field, type, value } = item as Record<string, unknown>;
    if (typeof field !== 'string' || !FIELD_NAME.test(field) || RESERVED_FIELD_NAMES.has(field) || seen.has(field)) {
      return { kind: 'invalid' };
    }
    if (type === 'enum' || !(FIELD_TYPES as readonly unknown[]).includes(type)) return { kind: 'invalid' };
    if (!valueFits(value, type as FieldType)) return { kind: 'invalid' };
    seen.add(field);
    fields.push({ field, type: type as FieldType, value });
  }
  return { kind: 'fields', fields };
}

/** Missing or malformed side-effect notes make a confirmation incomplete. */
export function cardNotesOf(t: TFunction<'assistant'>, params: Record<string, unknown>): { kind: 'notes'; notes: string[] } | { kind: 'invalid' } {
  if (!('cardNotes' in params)) return { kind: 'notes', notes: [] };
  const keys = params['cardNotes'];
  if (!Array.isArray(keys) || keys.length === 0 || new Set(keys).size !== keys.length) return { kind: 'invalid' };
  const notes: string[] = [];
  for (const key of keys) {
    if (typeof key !== 'string' || !/^[a-z][A-Za-z0-9]*$/.test(key)) return { kind: 'invalid' };
    const text = t(`confirmation.cardNotes.${key}`, { defaultValue: '' });
    if (typeof text !== 'string' || !text.trim() || /{{|}}/.test(text)) return { kind: 'invalid' };
    notes.push(text);
  }
  return { kind: 'notes', notes };
}

function recordFieldText(t: TFunction<'assistant'>, language: string, entry: FieldDefault): string {
  const field = t(`confirmation.fields.${entry.field}`, { defaultValue: entry.field });
  const value = valueText(t, language, entry.value, entry.type, entry.field);
  return t('confirmation.recordField', { replace: { field, value }, defaultValue: `${field}: ${value}` });
}

/**
 * #3879: what a generated delete does besides the record itself
 * (`payload.deleteEffects`): its mode, which must match the headline, and
 * every child the database removes or detaches with it, counted where the
 * preview showed them. A child whose noun is in no bundle, a bad count or a
 * mode that contradicts the headline fails closed: the human must see every
 * consequence or not be able to confirm.
 */
const DELETE_HEADLINES = {
  hard: 'confirmation.presenters.generatedDelete',
  deactivate: 'confirmation.presenters.generatedDeactivate',
} as const;
const NOUN_KEY = /^[a-z][A-Za-z]*$/;

interface DeleteChildLine {
  readonly noun: string;
  readonly effect: 'deleted' | 'detached';
  readonly count?: number;
}

type DeleteEffectsOf =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'effects'; readonly children: readonly DeleteChildLine[] };

function deleteEffectsOf(
  t: TFunction<'assistant'>,
  params: Record<string, unknown>,
  presenterKey: string | undefined,
): DeleteEffectsOf {
  if (!('deleteEffects' in params)) {
    // A delete headline without its effects would hide what goes with the record.
    const isDelete = (Object.values(DELETE_HEADLINES) as readonly string[]).includes(presenterKey ?? '');
    return isDelete ? { kind: 'invalid' } : { kind: 'absent' };
  }
  const raw = params['deleteEffects'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { kind: 'invalid' };
  const { mode, children } = raw as Record<string, unknown>;
  if (mode !== 'hard' && mode !== 'deactivate') return { kind: 'invalid' };
  if (presenterKey !== DELETE_HEADLINES[mode]) return { kind: 'invalid' };
  if (!Array.isArray(children)) return { kind: 'invalid' };
  if (mode === 'deactivate' && children.length > 0) return { kind: 'invalid' };
  const lines: DeleteChildLine[] = [];
  const missing = '\u0000missing';
  for (const item of children) {
    if (!item || typeof item !== 'object') return { kind: 'invalid' };
    const { nounKey, effect, count } = item as Record<string, unknown>;
    if (typeof nounKey !== 'string' || !NOUN_KEY.test(nounKey)) return { kind: 'invalid' };
    if (effect !== 'deleted' && effect !== 'detached') return { kind: 'invalid' };
    if (count !== undefined && (typeof count !== 'number' || !Number.isInteger(count) || count < 0)) {
      return { kind: 'invalid' };
    }
    const noun: unknown = t(`confirmation.nouns.${nounKey}`, { defaultValue: missing, returnObjects: true });
    if (typeof noun !== 'string' || noun === missing) return { kind: 'invalid' };
    lines.push({ noun, effect, ...(count !== undefined ? { count } : {}) });
  }
  return { kind: 'effects', children: lines };
}

function deleteChildText(t: TFunction<'assistant'>, language: string, line: DeleteChildLine): string {
  if (line.count === undefined) {
    return t(`confirmation.deleteEffects.${line.effect}`, { replace: { noun: line.noun }, defaultValue: line.noun });
  }
  const count = formatted(String(line.count), () => exactNumber(line.count ?? 0, language));
  return t(`confirmation.deleteEffects.${line.effect}Count`, {
    replace: { noun: line.noun, count },
    defaultValue: `${line.noun} (${count})`,
  });
}

function presentOrFallback(
  t: TFunction<'assistant'>,
  presenter: ConfirmationPresenter,
  description: string
): string {
  const params = withNoun(t, presenter.params);
  if (params === undefined) return description;
  const rendered: unknown = t(presenter.key, {
    replace: params,
    defaultValue: description,
    returnObjects: true,
  });
  if (typeof rendered !== 'string') return description;
  if (UNFILLED_PLACEHOLDER.test(rendered)) return description;
  return rendered;
}

export function ConfirmationCard({
  description,
  toolName,
  presenter,
  params,
  isLoading,
  retry,
  onConfirm,
  onCancel,
}: ConfirmationCardProps) {
  const { t, i18n } = useTranslation('assistant');
  const { t: tc } = useTranslation('common');

  // Nothing but a finished translation or the server's summary may reach the
  // screen. Three guards, each for a case a well-typed presenter cannot rule
  // out on its own:
  //  - `replace` carries the params, so a param named like an i18next option
  //    (`lng`, `count`, `context`, `returnDetails`, `defaultValue_other`…)
  //    interpolates instead of steering the lookup;
  //  - `defaultValue` is what i18next returns for a key in no bundle
  //    (fallback language included) — the unknown-key case;
  //  - a non-string result (a parent key with `returnObjects`) or a
  //    placeholder the params did not fill (`{{lastName}}`) is not a
  //    translation, and falls back to the summary rather than showing.
  const text = presenter ? presentOrFallback(t, presenter, description) : description;
  const diff = changesOf(params);
  const changes = diff.kind === 'diff' ? diff.changes : [];
  const fieldDefaults = defaultsOf(params);
  const defaults = fieldDefaults.kind === 'defaults' ? fieldDefaults.defaults : [];
  const pinnedInfo = pinnedFieldsOf(params);
  const pinned = pinnedInfo.kind === 'defaults' ? pinnedInfo.defaults : [];
  const recordInfo = recordFieldsOf(params);
  const recordFields = recordInfo.kind === 'fields' ? recordInfo.fields : [];
  const effects = deleteEffectsOf(t, params, presenter?.key);
  const cardNotes = cardNotesOf(t, params);
  const childLines = effects.kind === 'effects' ? effects.children : [];
  // #3917: a generated create's lists (items, questions), element by element.
  const listInfo = listFieldsOf(t, params);
  const listSections = listInfo.kind === 'lists' ? listInfo.lists : [];
  // An enum default never reaches a card (admission refuses one); a changed or pinned enum needs its labels.
  const enumsReadable = enumsLabelled(t, [
    ...changes.map((change) => ({ field: change.field, type: change.type, values: [change.from, change.to] })),
    ...pinned.map((entry) => ({ field: entry.field, type: entry.type, values: [entry.value] })),
    ...defaults.map((entry) => ({ field: entry.field, type: entry.type, values: [entry.value] })),
  ]);
  const diffInvalid =
    diff.kind === 'invalid' ||
    listInfo.kind === 'invalid' ||
    fieldDefaults.kind === 'invalid' ||
    pinnedInfo.kind === 'invalid' ||
    !enumsReadable ||
    recordInfo.kind === 'invalid' ||
    effects.kind === 'invalid' || cardNotes.kind === 'invalid';
  const quoteReview = parseQuoteConfirmationReview(params, presenter?.key);
  const workReview = parseWorkReview(params, presenter?.key, toolName);
  const sendReview = parseQuoteSendReview(params, presenter?.key);
  const serviceLineChange = parseQuoteServiceLineReview(params, presenter?.key, toolName);
  const serviceAdd = parseQuoteServiceAddReview(params, presenter?.key, toolName);
  const lineChange = parseQuoteItemChangeReview(params, presenter?.key);
  // A quote acceptance needs the receipt time the staff member confirms on
  // the card; Confirm stays disabled until it is valid (#2821).
  const [receivedAt, setReceivedAt] = useState<string | null>(null);
  const needsReceipt = quoteReview.kind === 'review' && quoteReview.review.action === 'accept';

  return (
    <Box sx={{ mb: 1.5, ml: 4.5 }}>
      <Card
        variant="outlined"
        sx={{
          maxWidth: '85%',
          borderColor: 'warning.main',
          borderWidth: 1,
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="warning.main" fontWeight={600}>
            {t('confirmation.title')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5 }}>
            {text}
          </Typography>
          {(recordFields.length > 0 || childLines.length > 0) && (
            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
              {recordFields.map((entry) => (
                <Typography component="li" variant="body2" key={`record-${entry.field}`}>
                  {recordFieldText(t, i18n.language, entry)}
                </Typography>
              ))}
              {childLines.map((line) => (
                <Typography component="li" variant="body2" key={`child-${line.noun}`} color="warning.main">
                  {deleteChildText(t, i18n.language, line)}
                </Typography>
              ))}
            </Box>
          )}
          {cardNotes.kind === 'notes' && cardNotes.notes.map((note, index) => (
            <Typography key={`card-note-${String(index)}`} variant="body2" color="warning.main" sx={{ mb: 1.5 }}>{note}</Typography>
          ))}
          {params.noteKey === 'autonomyRulesInert' && <Typography variant="body2" sx={{ mb: 1.5 }}>{t('confirmation.notes.autonomyRulesInert')}</Typography>}
          {(changes.length > 0 || defaults.length > 0 || pinned.length > 0) && (
            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
              {changes.map((change) => (
                <Typography component="li" variant="body2" key={change.field}>
                  {changeText(t, i18n.language, change)}
                </Typography>
              ))}
              {defaults.map((entry) => (
                <Typography component="li" variant="body2" key={`default-${entry.field}`}>
                  {defaultText(t, i18n.language, entry)}
                </Typography>
              ))}
              {pinned.map((entry) => (
                <Typography component="li" variant="body2" key={`pinned-${entry.field}`}>
                  {pinnedText(t, i18n.language, entry)}
                </Typography>
              ))}
            </Box>
          )}
          {listSections.length > 0 && !diffInvalid && (
            <ConfirmationListFields
              t={t}
              lists={listSections}
              valueText={(value, type) => valueText(t, i18n.language, value, type as FieldType)}
            />
          )}
          {(workReview.kind === 'ownTime' || workReview.kind === 'execution' || workReview.kind === 'timer') && (
            <WorkConfirmationReview value={workReview} />
          )}
          {workReview.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {t('workReview.unavailable')}
            </Typography>
          )}
          {quoteReview.kind === 'review' && (
            <QuoteConfirmationReview
              review={quoteReview.review}
              onReceiptChange={setReceivedAt}
              retry={retry}
            />
          )}
          {quoteReview.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{t('quoteReview.unavailable')}</Typography>
          )}
          {sendReview.kind === 'review' && <QuoteSendConfirmationReview review={sendReview.review} />}
          {sendReview.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {t('quoteSend.unavailable')}
            </Typography>
          )}
          {serviceLineChange.kind === 'review' && <QuoteServiceLineChangeReview review={serviceLineChange.review} />}
          {serviceAdd.kind === 'review' && <QuoteServiceAddReview review={serviceAdd.review} />}
          {serviceAdd.kind === 'invalid' && <Typography variant="body2" color="error">{t('quoteServiceAdd.unavailable')}</Typography>}
          {serviceLineChange.kind === 'invalid' && <Typography variant="body2" color="error">{t('quoteServiceLine.unavailable')}</Typography>}
          {lineChange.kind === 'review' && <QuoteItemChangeReview review={lineChange.review} />}
          {lineChange.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{t('quoteItemChange.unavailable')}</Typography>
          )}
          {retry?.reason === 'retryable' && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }} role="status">
              {t('confirmation.retryable')}
            </Typography>
          )}
          {diffInvalid && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {t('confirmation.diffUnavailable')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() =>
                { onConfirm(needsReceipt && receivedAt ? { approvalReceivedAt: receivedAt } : undefined); }
              }
              disabled={
                isLoading ||
                diffInvalid ||
                quoteReview.kind === 'invalid' ||
                lineChange.kind === 'invalid' ||
                serviceLineChange.kind === 'invalid' ||
                serviceAdd.kind === 'invalid' ||
                workReview.kind === 'invalid' ||
                sendReview.kind === 'invalid' ||
                (needsReceipt && receivedAt === null)
              }
            >
              {retry?.reason === 'retryable' ? t('confirmation.retryButton') : tc('confirm')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<CancelOutlinedIcon />}
              onClick={onCancel}
              disabled={isLoading}
            >
              {tc('cancel')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
